import { z } from 'zod';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { ModelName } from '@casops/store/api';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useModelForm from '@casops/components/layout/model-form/useModelForm';
import {
  CreateGeofenceAreaModel,
  EditGeofenceAreaModel,
  GeofenceAreaModel,
  GeofenceAreaFormModel,
  useCreateGeofenceAreaMutation,
  useEditGeofenceAreaMutation,
} from '@casops/store/apis/geofenceAreasApi';
import { ValidNameRegex } from '@casops/store/apis/commonModels';
import { ServerErrorFields } from '@dmc/ui';

type DataModel = GeofenceAreaModel;
type FormModel = GeofenceAreaFormModel;
type CreateModel = CreateGeofenceAreaModel;
type EditModel = EditGeofenceAreaModel;

const modelName = ModelName.GeofenceArea;

const DESCRIPTION_MAX = 255;

const defaultValues: FormModel = {
  name: '',
  desc: '',
};

const serverErrorFields: ServerErrorFields<FormModel> = {
  name: 'Name',
  desc: 'Description',
};

interface AreaFormProps {
  item?: DataModel;
}

export const AreaForm = ({ item }: AreaFormProps): ReactElement => {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    return z.object({
      name: z
        .string()
        .min(1, { message: t('Validation.SpecifyAreaName') })
        .max(128, { message: t('Validation.AreaNameTooLong') })
        .regex(ValidNameRegex, { message: t('Validation.InvalidCharacters') }),
      desc: z.string().max(DESCRIPTION_MAX, {
        message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
      }),
    });
  }, [t]);

  const [triggerCreateItem] = useCreateGeofenceAreaMutation();
  const [triggerEditItem] = useEditGeofenceAreaMutation();

  const { canSubmit, onSubmit, errorMessage, control } = useModelForm<
    DataModel,
    FormModel,
    CreateModel,
    EditModel
  >({
    modelName,
    defaultValues,
    item,
    schema,
    triggerCreateItem,
    triggerEditItem,
    serverErrorFields,
  });

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
    >
      <>
        <CasFormInput
          key="name"
          control={control}
          name="name"
          label={t('Fields.AreaName')}
          disabled={item?.fixed}
        />
        <CasFormInput
          key={'desc'}
          control={control}
          name="desc"
          label={t('Fields.Description')}
          element="textarea"
          minHeight="100px"
        />
      </>
    </ModelForm>
  );
};

export default AreaForm;
